import { Journey } from '@audibene/ta.utils-opportunity'
import { addYears, differenceInBusinessDays, differenceInCalendarDays } from 'date-fns'
import { useCallback, useEffect, useState, VFC } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router'
import {
  AppointmentParameters,
  AppointmentType,
  Country,
  EventType,
  Route,
  RouteParameters,
  getRequiredAppointmentParameters,
  isShopGidOptionalForPrsFlow,
} from '../../../../constants'
import {
  ClickTrackingProvider,
  FormAnalyticsProvider,
  IsManualModeProvider,
  SchedulingStatus,
  SearchParameters,
  SessionDurationProvider,
  useAcfuActingUser,
  useAppointmentLocationState,
  useAppointmentTypes,
  useAvailabilitiesHookName,
  useBlockedAppointmentType,
  useCustomer,
  useDoubleBookingContext,
  useEventTracking,
  useFeatureFlags,
  useFirstAvailability,
  useFormAnalytics,
  useIsManualMode,
  useNotification,
  useNow,
  useReservations,
  useScheduleFormDefaultValues,
  useSchedulingState,
  useSearchParameters,
  useShop,
  useTimeZones,
  useTrackError,
} from '../../../../hooks'
import { useAppointmentsService } from '../../../../hooks/services'
import { AppointmentCreateRequest, AppointmentFormData, AppointmentTypes, Customer, EventData, ReservationStatus } from '../../../../types'
import {
  appendSearchParamsToPathname,
  hydratePathname,
  isAnyResourceId,
  isSlotUnavailableError,
  logFactory,
  selectRandomItem,
} from '../../../../util'
import { Schedule as SharedSchedule } from '../../../schedule'
import { handleSubmissionFailure, trackAcfuBookingOutcome } from '../../util'
import { Invalid } from './Invalid'
import { Loading } from './Loading'

const log = logFactory('AppointmentStandaloneForm')

interface ContentProps {
  appointmentTypes: AppointmentTypes
  customer: Customer
  defaultValues: AppointmentFormData
}

const Content = ({ appointmentTypes, customer, defaultValues }: ContentProps) => {
  const navigate = useNavigate()
  const { data: now = new Date() } = useNow()
  const { notify } = useNotification()
  const { readSummary, reset } = useFormAnalytics<AppointmentFormData>()
  const { createAppointment } = useAppointmentsService()
  const { track: trackEvent } = useEventTracking()
  const client = useQueryClient()
  const trackError = useTrackError()
  const { isManualMode } = useIsManualMode()
  const doubleBookingContext = useDoubleBookingContext()
  const { blockedAppointmentTypes } = useBlockedAppointmentType()
  const { firstAvailability } = useFirstAvailability()
  const [isAvailabilityCacheEnabled, isAcfuProgressiveDisclosureEnabled] = useFeatureFlags<boolean>([
    'ENABLE_AVAILABILITY_CACHE',
    'ACFU_PROGRESSIVE_DATE_DISCLOSURE',
  ])
  const { actingUserId, actingUserRole } = useAcfuActingUser()

  const customerDefaultTimeZone = customer?.timezone as string

  const onSubmit = useCallback(
    async (data: AppointmentFormData): Promise<void> => {
      log('📆 Transforming form data...', data)

      const type = data.type as AppointmentType
      const country = data.country as Country
      const journey = data.journey as Journey
      const tmpResourceIds = data?.resource_ids as string[]
      const eligibleResourcePairs = data?.eligibleResourcePairs ?? []
      const isAnyoneSelected = tmpResourceIds?.some((resourceId) => isAnyResourceId(resourceId))
      const scheduledCustomerTimezone = data?.customer_timezone

      const resourceIds: string[] =
        isAnyoneSelected && eligibleResourcePairs.length > 0
          ? (selectRandomItem(eligibleResourcePairs).filter(Boolean) as string[])
          : tmpResourceIds
      const startDate = data.start_date as Date

      const request: AppointmentCreateRequest = {
        country,
        customer_salesforce_id: data?.customer_salesforce_id as string,
        duration_in_minutes: data?.duration_in_minutes as number,
        is_manual: isManualMode,
        journey,
        opportunity_gid: data?.opportunity_gid as string,
        resource_ids: resourceIds,
        scheduled_customer_timezone: scheduledCustomerTimezone as string,
        start_date: startDate,
        title: data.title as string,
        type,
      }
      const { didError, errorCounts, totalClicks, totalSessionDurationInSeconds, submitCount, validInputTimings } = readSummary()

      const isFirstAvailability =
        firstAvailability?.start_date?.getTime() === startDate.getTime() &&
        resourceIds.length === firstAvailability?.resource_ids?.length &&
        resourceIds.every((resourceId, idx) => firstAvailability?.resource_ids?.[idx] === resourceId)

      const daysInTheFutureBusinessFirstAvailability = firstAvailability?.start_date
        ? differenceInBusinessDays(firstAvailability.start_date, now)
        : undefined

      const eventData: EventData = {
        customerDefaultTimeZone,
        customerTimeZone: data.customer_timezone as string,
        daysInTheFuture: differenceInCalendarDays(startDate, now),
        daysInTheFutureBusiness: differenceInBusinessDays(startDate, now),
        daysInTheFutureBusinessFirstAvailability,
        didError,
        durationDefaultInMinutes: appointmentTypes?.[type]?.duration_in_minutes,
        durationInMinutes: data?.duration_in_minutes as number,
        errorCountResourceNotSelected: errorCounts['resource_ids'] ?? 0,
        errorCountStartDayNotSelected: errorCounts['clientStartDate'] ?? 0,
        errorCountTimePickerInvalid: errorCounts['clientStartTime'] ?? 0,
        isFirstAvailability,
        isAnyoneSelected,
        formType: 'initial',
        resourceTimeZone: data?.fitter_timezone as string,
        secondsToValidTime: validInputTimings['clientStartTime'],
        startDate: startDate.toISOString(),
        startDay: startDate.getDate(),
        startDayOfWeek: startDate.getDay(),
        startTimeHour: Number(
          startDate.toLocaleTimeString('en', {
            timeZone: data.customer_timezone as string,
            hour: 'numeric',
            hour12: false,
          })
        ),
        startTimeMinute: Number(
          startDate.toLocaleTimeString('en', {
            timeZone: data.customer_timezone as string,
            minute: 'numeric',
            hour12: false,
          })
        ),
        submissionCount: submitCount + 1, // We only increment submission count when validation fails, so we need to bump one for passing validation
        totalClicks,
        totalSessionDurationInSeconds,
        type: type as string,
        // wasTimePickerUsed: false, // TODO: Read from a TBD context
      }

      log('📩 Submitting appointment...', request)

      try {
        const created = await createAppointment(request)

        trackEvent(EventType.APPOINTMENT_FORM_SUBMISSION_SUCCEEDED, {
          data: eventData,
        })

        // This file is the booking-only submission handler (rescheduling an existing appointment
        // goes through Appointment/Reschedule/New/Content instead), so isRescheduling is always false.
        trackAcfuBookingOutcome({
          actingUserId,
          actingUserRole,
          data,
          eventData,
          isAcfuProgressiveDisclosureEnabled,
          isRescheduling: false,
          trackEvent,
        })

        const pathname = hydratePathname(Route.CONSULTANT_APPOINTMENT_SCHEDULE_SUCCESS, {
          [RouteParameters.COUNTRY]: created.country,
          [RouteParameters.APPOINTMENT_ID]: created.id,
        })

        navigate(pathname, { replace: true })
      } catch (error: any) {
        const failedEventData = {
          data: eventData,
          type: EventType.APPOINTMENT_FORM_SUBMISSION_FAILED,
        }
        const invalidateQueries = () => client.invalidateQueries([useAvailabilitiesHookName])

        handleSubmissionFailure({
          error,
          eventData: failedEventData,
          invalidateQueries,
          notify,
          trackError,
          trackEvent,
        })

        if (isSlotUnavailableError(error)) {
          trackEvent(EventType.APPOINTMENT_FORM_SUBMISSION_FAILED_SLOT_UNAVAILABLE, {
            data: {
              ...eventData,
              wasAvailabilityCacheEnabled: isAvailabilityCacheEnabled,
            },
          })
        }

        // Rethrow so our form's isSubmitSuccessful isn't set to true
        throw error
      }
    },
    [
      actingUserId,
      actingUserRole,
      appointmentTypes,
      createAppointment,
      customerDefaultTimeZone,
      firstAvailability,
      isAcfuProgressiveDisclosureEnabled,
      isAvailabilityCacheEnabled,
      navigate,
      notify,
      now,
      readSummary,
      reset,
      trackEvent,
      trackError,
    ]
  )

  if (doubleBookingContext.isLoading) {
    return <Loading />
  } else {
    if (doubleBookingContext.isHearingAidEvaluationEnabled && doubleBookingContext.hasCompletedHearingAidEvaluation) {
      // This runs *after* useScheduleFormDefaultValues, so it overwrites whatever the trial gate
      // substituted and would otherwise walk the forced type straight past the gate: a completed
      // HAE makes nextAppointmentType FOLLOW_UP, which is exactly what the out-of-trial direction
      // blocks. Nothing downstream re-checks - validationSchema validates `type` against every
      // AppointmentType and knows nothing about the gate - so the booking would submit, with the
      // substitution note silent because the selected type is the blocked one rather than the
      // substituted one. Leave the substituted default in place instead of forcing past it.
      const isForcedTypeBlocked = blockedAppointmentTypes.includes(doubleBookingContext.nextAppointmentType as AppointmentType)

      if (!isForcedTypeBlocked) {
        defaultValues.type = doubleBookingContext.nextAppointmentType
      }
    }
  }

  return <SharedSchedule defaultValues={defaultValues} onSubmit={onSubmit} />
}

const Schedule: VFC = () => {
  const defaultValues = useScheduleFormDefaultValues()
  const searchParameters = useSearchParameters()
  const appointmentLocationState = useAppointmentLocationState()
  const { customer, isLoading: isCustomerLoading } = useCustomer()
  const { shop, isLoading: isResourceLoading } = useShop()
  const { appointmentTypes, isLoading: areAppointmentTypesLoading } = useAppointmentTypes()
  const { timeZones, isLoading: areTimeZonesLoading } = useTimeZones()
  const { setStatus: setSchedulingStatus } = useSchedulingState()
  const navigate = useNavigate()
  const [isReservationEnabledFeatureFlag] = useFeatureFlags<boolean>(['ENABLE_RESERVATION_WITH_PROVIDER_RECOMMENDATION'])
  const leadGid = searchParameters.lead_gid as string
  // using useNow() causes an infinite rendering loop. TODO: Figure out why.
  const [now] = useState(new Date())
  const { data: existingReservations, isLoading: areReservationsLoading } = useReservations(
    {
      expires_at: [now, addYears(now, 1)],
      lead_gid: [leadGid],
      status: [ReservationStatus.CONFIRMED],
    },
    Boolean(leadGid)
  )

  const appointmentParameters = {
    ...searchParameters,
    ...appointmentLocationState,
  }

  const { country, journey } = appointmentParameters
  const isLoading = isCustomerLoading || isResourceLoading || areAppointmentTypesLoading || areTimeZonesLoading || areReservationsLoading
  const isParameterMissing = (parameter: AppointmentParameters) => typeof appointmentParameters?.[parameter] === 'undefined'
  const invalidParameters = getRequiredAppointmentParameters(appointmentParameters.journey as string, country as string).filter(
    isParameterMissing
  )

  useEffect(() => {
    if (isLoading) return

    setSchedulingStatus(SchedulingStatus.FETCHING)
  }, [isLoading, setSchedulingStatus])

  if (isLoading) {
    return <Loading />
  }

  if (invalidParameters.length) {
    return <Invalid invalidParameters={invalidParameters} />
  }

  // If the lead parameter was sent and the reservations feature flag is enabled,
  // check for existing reservations and redirect
  if (isReservationEnabledFeatureFlag && existingReservations?.length) {
    const pathname = hydratePathname(Route.RESERVATION_SUCCESS, {
      [RouteParameters.COUNTRY]: existingReservations[0].country,
      [RouteParameters.RESERVATION_ID]: existingReservations[0].id,
    })

    const shopGid = searchParameters.shop_gid ?? shop?.gid
    const searchParams = {
      [SearchParameters.CUSTOMER_GID]: searchParameters.customer_gid as string,
      [SearchParameters.OPPORTUNITY_GID]: searchParameters.opportunity_gid as string,
      [SearchParameters.JOURNEY]: searchParameters.journey as string,
      ...(shopGid ? { [SearchParameters.SHOP_GID]: shopGid } : {}),
    }

    const pathnameAndSearch = appendSearchParamsToPathname({
      pathname,
      searchParams,
    })

    navigate(pathnameAndSearch, { replace: true })

    return <></>
  }

  const missingDataErrors = Object.entries({
    appointmentTypes: !appointmentTypes && `Could not load appointment types: ${country} / ${journey}`,
    customer: !customer && `Could not find a customer with gid: ${appointmentParameters.customer_gid}`,
    customerTimeZone:
      customer && !customer?.timezone && `Customer with gid: ${appointmentParameters.customer_gid} is missing time zone information`,
    shop:
      !shop &&
      !isShopGidOptionalForPrsFlow(journey as string, country as string) &&
      `Could not find a shop${appointmentParameters.shop_gid ? ` with gid: ${appointmentParameters.shop_gid}` : ''}`,
    timeZones: !timeZones && `Could not load time zones`,
  })
    .filter(([, value]) => Boolean(value))
    .reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: value as string,
      }),
      {}
    )

  if (Object.keys(missingDataErrors).length > 0) {
    return <Invalid missingDataErrors={missingDataErrors} />
  }

  // After all error states are handled, the only reason defaultValues can still be null is the
  // cabin-only calendar summaries race condition in useScheduleFormDefaultValues.
  if (!defaultValues) {
    return <Loading />
  }

  return (
    <SessionDurationProvider>
      <ClickTrackingProvider>
        <IsManualModeProvider>
          <FormAnalyticsProvider<AppointmentFormData>>
            <Content
              appointmentTypes={appointmentTypes as AppointmentTypes}
              customer={customer as Customer}
              defaultValues={defaultValues as AppointmentFormData}
            />
          </FormAnalyticsProvider>
        </IsManualModeProvider>
      </ClickTrackingProvider>
    </SessionDurationProvider>
  )
}

export { Content, Schedule }
